import type { GraphLegendItem } from '@/src/components/GraphLegends.types'
import { GranularityEnum } from '@/src/enums/granularity.enum'
import { colorMap } from '@/src/features/procurement-and-markets/portfolio/components/diagram/portfolioDiagram.consts'
import type {
  CurrentFixesDataPoint,
  PortfolioDataPoint,
  TotalPortfolioDataPoint,
} from '@/src/features/procurement-and-markets/portfolio/components/diagram/portfolioDiagram.types'
import { PortfolioView, UnitView } from '@/src/features/procurement-and-markets/portfolio/components/diagram/portfolioDiagram.types'
import { isNullOrUndefined } from '@/src/shared/utils'
import { format } from 'date-fns'
import type { TFunction } from 'i18next'

const POSITIVE_KEYS: Record<PortfolioView, string[]> = {
  [PortfolioView.TOTAL_PORTFOLIO]: ['tranches', 'ppas', 'pacs', 'spotBought'],
  [PortfolioView.CURRENT_FIXES]: ['tranches', 'ppas', 'pacs', 'planned', 'spotBought'],
}

export const getChartMaxValue = (dataPoints: Record<string, unknown>[], portfolioView: PortfolioView): number => {
  const keys = POSITIVE_KEYS[portfolioView]
  return Math.max(0, ...dataPoints.map(p => keys.reduce((sum, k) => sum + ((p[k] as number | null) ?? 0), 0)))
}

export const getChartMinValue = (dataPoints: Pick<PortfolioDataPoint, 'spotSold'>[]): number => {
  return Math.min(0, ...dataPoints.map(p => -(p.spotSold ?? 0)))
}

export const checkDoesValueExistInCurve = <T>(propertyName: keyof T, dataPoints: T[]) => {
  return dataPoints.some((point: T) => !isNullOrUndefined(point[propertyName]))
}

export const getTotalPortfolioLegends = (dataPoints: TotalPortfolioDataPoint[], unitView: UnitView, t: TFunction) => {
  const legends: GraphLegendItem[] = []

  if (unitView !== UnitView.COST && checkDoesValueExistInCurve('actualConsumption', dataPoints)) {
    legends.push({
      text: t('components.procurement.portfolio_diagram.actual_consumption'),
      lineColor: colorMap.actualConsumption,
      iconType: 'line',
    })
  }
  if (unitView !== UnitView.COST && checkDoesValueExistInCurve('plannedConsumption', dataPoints)) {
    legends.push({
      text: t('components.procurement.portfolio_diagram.expected_consumption'),
      lineColor: colorMap.plannedConsumption,
      iconType: 'dashed-line',
    })
  }
  if (checkDoesValueExistInCurve('tranches', dataPoints)) {
    legends.push({ text: t('components.procurement.portfolio_diagram.terminmarkt'), lineColor: colorMap.tranches, iconType: 'box' })
  }
  if (checkDoesValueExistInCurve('pacs', dataPoints)) {
    legends.push({ text: t('components.procurement.portfolio_diagram.fahrplan'), lineColor: colorMap.pacs, iconType: 'box' })
  }
  if (checkDoesValueExistInCurve('ppas', dataPoints)) {
    legends.push({ text: t('components.procurement.portfolio_diagram.ppas'), lineColor: colorMap.ppas, iconType: 'box' })
  }
  if (checkDoesValueExistInCurve('spotBought', dataPoints)) {
    legends.push({ text: t('components.procurement.portfolio_diagram.spot_bought'), lineColor: colorMap.spotBought, iconType: 'box' })
  }
  if (checkDoesValueExistInCurve('spotSold', dataPoints)) {
    legends.push({ text: t('components.procurement.portfolio_diagram.spot_sold'), lineColor: colorMap.spotSold, iconType: 'box' })
  }

  return legends
}

export const getCurrentFixesLegends = (dataPoints: CurrentFixesDataPoint[], unitView: UnitView, t: TFunction) => {
  const legends: GraphLegendItem[] = []

  if (unitView !== UnitView.COST && checkDoesValueExistInCurve('actualConsumption', dataPoints)) {
    legends.push({
      text: t('components.procurement.portfolio_diagram.actual_consumption'),
      lineColor: colorMap.actualConsumption,
      iconType: 'line',
    })
  }
  if (unitView !== UnitView.COST && checkDoesValueExistInCurve('plannedConsumption', dataPoints)) {
    legends.push({
      text: t('components.procurement.portfolio_diagram.expected_consumption'),
      lineColor: colorMap.plannedConsumption,
      iconType: 'dashed-line',
    })
  }
  if (checkDoesValueExistInCurve('tranches', dataPoints)) {
    legends.push({ text: t('components.procurement.portfolio_diagram.terminmarkt_fixed'), lineColor: colorMap.tranches, iconType: 'box' })
  }
  if (checkDoesValueExistInCurve('ppas', dataPoints)) {
    legends.push({ text: t('components.procurement.portfolio_diagram.ppa_fixed'), lineColor: colorMap.ppas, iconType: 'box' })
  }
  if (checkDoesValueExistInCurve('pacs', dataPoints)) {
    legends.push({ text: t('components.procurement.portfolio_diagram.fahrplan_fixed'), lineColor: colorMap.pacs, iconType: 'box' })
  }
  if (checkDoesValueExistInCurve('planned', dataPoints)) {
    legends.push({ text: t('components.procurement.portfolio_diagram.planned'), lineColor: colorMap.planned, iconType: 'box' })
  }
  if (checkDoesValueExistInCurve('spotBought', dataPoints)) {
    legends.push({ text: t('components.procurement.portfolio_diagram.spot_bought'), lineColor: colorMap.spotBought, iconType: 'box' })
  }
  if (checkDoesValueExistInCurve('spotSold', dataPoints)) {
    legends.push({ text: t('components.procurement.portfolio_diagram.spot_sold'), lineColor: colorMap.spotSold, iconType: 'box' })
  }

  return legends
}

export const getGranularityOptions = (energyType: 'ELECTRICITY' | 'GAS', unitView: UnitView, t: TFunction) => {
  const isLoadProfileDisabled = unitView === UnitView.COST
  if (energyType === 'GAS') {
    return [
      { value: GranularityEnum.ONE_HOUR, label: t('load_profile'), isDisabled: isLoadProfileDisabled },
      { value: GranularityEnum.ONE_MONTH, label: t('reports.modules.granularities.monthly') },
    ]
  }
  return [
    { value: GranularityEnum.FIFTEEN_MINUTES, label: t('load_profile'), isDisabled: isLoadProfileDisabled },
    { value: GranularityEnum.ONE_MONTH, label: t('reports.modules.granularities.monthly') },
  ]
}

const getTooltipFormat = (granularity: GranularityEnum) => {
  switch (granularity) {
    case GranularityEnum.FIFTEEN_MINUTES:
    case GranularityEnum.ONE_HOUR:
      return 'dd. MMM yyyy HH:mm'
    case GranularityEnum.ONE_DAY:
      return 'dd. MMM yyyy'
    default:
      return 'MMM yyyy'
  }
}

const WEEKDAY_KEYS_BY_GET_DAY = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const

export const formatPortfolioTooltipTimestamp = (timestamp: Date | number | string, granularity: GranularityEnum, t: TFunction): string => {
  const date = new Date(timestamp)
  const dateLabel = format(date, getTooltipFormat(granularity))

  if (
    granularity !== GranularityEnum.FIFTEEN_MINUTES &&
    granularity !== GranularityEnum.ONE_HOUR &&
    granularity !== GranularityEnum.ONE_DAY
  ) {
    return dateLabel
  }

  const weekdayKey = WEEKDAY_KEYS_BY_GET_DAY[date.getDay()]
  const weekdayLabel = t(`components.procurement.portfolio_diagram.weekdays.${weekdayKey}`)

  return `${weekdayLabel}, ${dateLabel}`
}
